import { useMemo, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { AvailableSettings, BulkEmailExemptPolicy, DomainEntry } from 'types/Settings'

import { patchAccountLightweightSettings, PatchOperation } from 'redux/features/settings/settingsApiThunks'

export interface State {
  bulkEmailTableData: any[]
}

export interface EventHandlers {
  onAddItem: (item: DomainEntry) => void
  onRemoveItem: (id: string) => void
}

export type UseBulkEmailExemptLogic = [State, EventHandlers]

export const useBulkEmailExemptLogic = (): UseBulkEmailExemptLogic => {
  const dispatch = useAppDispatch()

  const { accountSettings } = useAppSelector(_stores => ({
    accountSettings: _stores.settings.accountSettings
  }))

  const transformToDomainEntries = (entries: BulkEmailExemptPolicy[]): DomainEntry[] =>
    entries.map(entry => ({
      domain: entry.name,
      comment: entry.comment,
      id: entry.id
    }))

  const bulkEmailTableData = useMemo(() => {
    const parsedExemptions = (accountSettings.bulk_email_sender_exempt as BulkEmailExemptPolicy[]) || []
    return transformToDomainEntries(parsedExemptions)
  }, [accountSettings])

  const onAddItem = useCallback(
    (item: DomainEntry) => {
      dispatch(
        patchAccountLightweightSettings({
          operation: PatchOperation.ADD,
          value: {
            name: item.domain,
            comment: item.comment,
            id: item.id
          },
          variable: AvailableSettings.BULK_EMAIL_SENDER_EXEMPT
        })
      )
    },
    [dispatch]
  )

  const onRemoveItem = useCallback(
    (id: string) => {
      dispatch(
        patchAccountLightweightSettings({
          operation: PatchOperation.REMOVE,
          value: { id },
          variable: AvailableSettings.BULK_EMAIL_SENDER_EXEMPT
        })
      )
    },
    [dispatch]
  )

  return useMemo(
    () => [
      {
        bulkEmailTableData
      },
      {
        onAddItem,
        onRemoveItem
      }
    ],
    [bulkEmailTableData, onAddItem, onRemoveItem]
  )
}
